/**
 * 1.6.0 My account → "AI assistant access" (plan A3, A5). Stream T's file (components/ChangePassword.tsx's
 * AccountDialog only mounts the tab when useAssistantTabVisible says so; docs/dev/mcp-1.6.0-build.md):
 *
 *  - create a token (name, expiry 30/90/365 days within the workspace's maximum, read-only on by default, "Write log
 *    descriptions" off by default), shown once with the setup command (`prism-mcp setup --url … --token …`);
 *  - list, rename and revoke the person's tokens;
 *  - the same install instructions as Administration → Integrations, for the person's own OS (lib/mcp-install.ts).
 */
import { useState } from 'react';
import { Check, Copy, KeyRound, Loader2, Pencil, Plus, ShieldOff, X } from 'lucide-react';
import { USER_TOKEN_EXPIRY_DAYS, type CreatedUserTokenDTO, type UserTokenDTO } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useCreateToken, useMyAssistantAccess, useMyTokens, useRevokeToken, useUpdateToken, type Me } from '../../api/hooks';
import { StatusDot } from '../../components/Status';
import { toast } from '../../components/Toasts';
import { copy } from '../../lib/clipboard';
import { relative } from '../../lib/format';
import { McpInstall } from '../admin/integrations/McpInstall';

/** Whether My account shows the tab: the workspace allows assistant access (useMyAssistantAccess), in a browser session. */
export function useAssistantTabVisible(me: Me): boolean {
  const q = useMyAssistantAccess(Boolean(me.session));
  return Boolean(me.session && q.data?.enabled);
}

/** The expiry choices within the workspace's maximum; with a maximum under 30 days, one choice of that many days. */
export function expiryChoices(maxDays: number): Array<{ value: 30 | 90 | 365; label: string }> {
  const label = (d: number) => (d === 365 ? 'In a year' : `In ${d} days`);
  const fit = USER_TOKEN_EXPIRY_DAYS.filter((d) => d <= maxDays).map((d) => ({ value: d, label: label(d) }));
  return fit.length ? fit : [{ value: 30, label: `In ${maxDays} day${maxDays === 1 ? '' : 's'}` }];
}

export function AssistantAccessTab({ me }: { me: Me }) {
  void me;
  const access = useMyAssistantAccess();
  const tokens = useMyTokens();
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<CreatedUserTokenDTO | null>(null);
  const a = access.data;
  const items = tokens.data?.items ?? [];
  const live = items.filter((t) => t.state === 'live');
  const old = items.filter((t) => t.state !== 'live');
  return (
    <div className="assistant-access" data-testid="assistant-access">
      <p className="muted" style={{ margin: 0 }}>Connect an AI assistant (Claude Desktop, Claude Code or another MCP client) to PRISM. The assistant works as you: it sees and changes only what you may,
        and what it changes is recorded as yours, via the assistant. It can never reach your account, Access, API keys or tokens.</p>
      {a && !a.canCreate && <div className="banner" data-testid="assistant-cannot-create"><div className="banner-body"><strong>Your role may not create assistant tokens</strong><span>Ask an administrator (Administration → Access → AI assistants).</span></div></div>}
      {issued && <IssuedToken created={issued} onClose={() => setIssued(null)} />}
      {!issued && a?.canCreate && (creating
        ? <CreateToken maxDays={a.maxExpiryDays} readOnlyOnly={a.readOnlyOnly} onCancel={() => setCreating(false)} onCreated={(c) => { setCreating(false); setIssued(c); }} />
        : <div><button type="button" className="btn primary sm" onClick={() => setCreating(true)} data-testid="token-new"><Plus size={14} /> New token</button></div>)}
      <p className="section-label" style={{ marginTop: 8, marginBottom: 0 }}>Your tokens</p>
      {tokens.isLoading ? <div className="skeleton" style={{ height: 60 }} /> : live.length === 0 ? <p className="faint" style={{ margin: 0 }} data-testid="tokens-empty">No tokens in use.</p> : (
        <div className="assistant-tokens" data-testid="my-tokens">{live.map((t) => <TokenRow key={t.id} t={t} readOnlyOnly={a?.readOnlyOnly ?? false} />)}</div>
      )}
      {old.length > 0 && (
        <details className="assistant-old">
          <summary className="faint">Revoked and expired ({old.length})</summary>
          <div className="assistant-tokens">{old.map((t) => <TokenRow key={t.id} t={t} readOnlyOnly={a?.readOnlyOnly ?? false} />)}</div>
        </details>
      )}
      <details className="assistant-install" open={Boolean(issued)} data-testid="assistant-install">
        <summary><strong>Install the connector</strong></summary>
        <McpInstall token={issued?.secret} testId="account-mcp-install" />
      </details>
    </div>
  );
}

function CreateToken({ maxDays, readOnlyOnly, onCancel, onCreated }: { maxDays: number; readOnlyOnly: boolean; onCancel: () => void; onCreated: (c: CreatedUserTokenDTO) => void }) {
  const create = useCreateToken();
  const choices = expiryChoices(maxDays);
  const [name, setName] = useState('');
  const [days, setDays] = useState<30 | 90 | 365>(choices.find((c) => c.value === 90)?.value ?? choices[choices.length - 1]!.value);
  const [readOnly, setReadOnly] = useState(true);
  const [writeLog, setWriteLog] = useState(false);
  async function submit() {
    try {
      onCreated(await create.mutateAsync({ name: name.trim(), expiresInDays: days, readOnly: readOnly || readOnlyOnly, writeLog }));
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <div className="panel-card assistant-create" data-testid="token-create">
      <div className="field-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 12 }}>
        <div className="field"><label htmlFor="token-name">Name</label>
          <input id="token-name" className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="e.g. Claude Desktop on my laptop" data-testid="token-name" autoFocus /></div>
        <div className="field"><label htmlFor="token-expiry">Expires</label>
          <select id="token-expiry" className="select" value={days} onChange={(e) => setDays(Number(e.target.value) as 30 | 90 | 365)} data-testid="token-expiry">
            {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select></div>
      </div>
      <label className="toggle-row"><input type="checkbox" checked={readOnly || readOnlyOnly} disabled={readOnlyOnly} onChange={(e) => setReadOnly(e.target.checked)} data-testid="token-read-only" />
        <span><b>Read-only</b><br /><span className="faint" style={{ fontSize: 12 }}>{readOnlyOnly ? 'This workspace allows read-only tokens only.' : 'The assistant can search and read, but not change anything. Untick to let it update metadata, add to collections, run workflows and export, as you.'}</span></span></label>
      <label className="toggle-row"><input type="checkbox" checked={writeLog} onChange={(e) => setWriteLog(e.target.checked)} data-testid="token-write-log" />
        <span><b>Write log descriptions</b><br /><span className="faint" style={{ fontSize: 12 }}>The assistant may write descriptions into log layers as suggestions that a person accepts or rejects (needs a token that is not read-only). It never changes what a person wrote.</span></span></label>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn ghost sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn primary sm" disabled={!name.trim() || create.isPending} onClick={() => void submit()} data-testid="token-create-submit">
          {create.isPending ? <Loader2 size={13} className="spin" /> : <KeyRound size={13} />} Create token</button>
      </div>
    </div>
  );
}

function IssuedToken({ created, onClose }: { created: CreatedUserTokenDTO; onClose: () => void }) {
  return (
    <div className="panel-card assistant-issued" data-testid="token-issued">
      <div className="banner warning"><div className="banner-body"><strong>Copy the token now</strong><span>It is shown only once: PRISM keeps just a fingerprint of it. If it is lost, make a new one and revoke “{created.token.name}”.</span></div></div>
      <pre className="code-block" data-testid="token-secret">{created.secret}</pre>
      <p className="muted" style={{ margin: 0 }}>Run this on your computer once the connector is installed (the steps below include it):</p>
      <pre className="code-block" data-testid="token-setup">{created.setupCommand}</pre>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn sm" onClick={() => copy(created.secret)} data-testid="token-copy"><Copy size={13} /> Copy token</button>
        <button type="button" className="btn sm" onClick={() => copy(created.setupCommand)} data-testid="token-copy-setup"><Copy size={13} /> Copy setup command</button>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn primary sm" onClick={onClose} data-testid="token-done">Done</button>
      </div>
    </div>
  );
}

function TokenRow({ t, readOnlyOnly }: { t: UserTokenDTO; readOnlyOnly: boolean }) {
  const update = useUpdateToken();
  const revoke = useRevokeToken();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(t.name);
  const live = t.state === 'live';
  async function change(body: { name?: string; readOnly?: boolean; writeLog?: boolean }) {
    try {
      await update.mutateAsync({ id: t.id, ...body });
      setEditing(false);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  async function onRevoke() {
    if (!window.confirm(`Revoke “${t.name}”? The assistant using it stops working at once.`)) return;
    try {
      await revoke.mutateAsync(t.id);
      toast(`Revoked ${t.name}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <div className="assistant-token" data-testid="token-row" data-state={t.state}>
      <div className="assistant-token-main">
        {editing ? (
          <span className="assistant-token-rename">
            <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} aria-label="Token name" data-testid="token-rename-input" autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) void change({ name: name.trim() }); if (e.key === 'Escape') setEditing(false); }} />
            <button type="button" className="btn ghost sm icon" aria-label="Save name" disabled={!name.trim()} onClick={() => void change({ name: name.trim() })} data-testid="token-rename-save"><Check size={13} /></button>
            <button type="button" className="btn ghost sm icon" aria-label="Cancel" onClick={() => { setName(t.name); setEditing(false); }}><X size={13} /></button>
          </span>
        ) : (
          <strong data-testid="token-name-text">{t.name}</strong>
        )}
        <span className="mono faint" style={{ fontSize: 12 }}>{t.prefix}…</span>
        <StatusDot tone={live ? 'ready' : 'failed'}>{t.state}</StatusDot>
      </div>
      <div className="assistant-token-meta faint">
        {t.readOnly || readOnlyOnly ? 'Read-only' : 'Read and change'}{t.writeLog ? ' · writes log descriptions' : ''}
        {' · '}{t.lastUsedAt ? `last used ${relative(t.lastUsedAt)}` : 'never used'}
        {' · '}{t.revokedAt ? `revoked ${relative(t.revokedAt)}` : `${live ? 'expires' : 'expired'} ${relative(t.expiresAt)}`}
      </div>
      {live && (
        <div className="assistant-token-actions">
          {!editing && <button type="button" className="btn ghost sm" onClick={() => setEditing(true)} data-testid="token-rename"><Pencil size={13} /> Rename</button>}
          {!readOnlyOnly && <button type="button" className="btn ghost sm" disabled={update.isPending} onClick={() => void change({ readOnly: !t.readOnly })} data-testid="token-toggle-read-only">{t.readOnly ? 'Allow changes' : 'Make read-only'}</button>}
          <button type="button" className="btn ghost sm" disabled={update.isPending} onClick={() => void change({ writeLog: !t.writeLog })} data-testid="token-toggle-write-log">{t.writeLog ? 'Stop log descriptions' : 'Allow log descriptions'}</button>
          <button type="button" className="btn ghost sm danger" onClick={() => void onRevoke()} data-testid="token-revoke"><ShieldOff size={13} /> Revoke</button>
        </div>
      )}
    </div>
  );
}
